import classNames from "classnames";
import { ProductVariationsProps } from "../BuilderInterface";
import "./style.scss";
import { getCompoundStyle, getStateStyleForBuilder, getStylesFromInteractiveStates } from "../helpers";
import { NodeStates, ProductVariationsNode } from "../../build/types";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import Variation from "./Variation";

const BuilderProductVariations: React.FC<ProductVariationsProps> = ({
    node,
    actionCallbacks,
    variationData,
    builderProps,
    renderProps,
    formatCurrency,
    disabled,
    children,
    emptyState,
    isSimple
}: ProductVariationsProps) => {
    const pVarNode = node as ProductVariationsNode;
    const { id, attr, stateManager, text, titleStyles } = pVarNode;
    const formatC = formatCurrency || ((amount: number) => amount.toFixed(2));
    const productVariationsClass = classNames("product-variations", {
        [`${attr?.className}`]: !!attr?.className
    });
    const { setElement, states } = useInteractionStates<HTMLDivElement>();

    if (builderProps) {
        const { nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        return (
            <div
                {...attr}
                className={productVariationsClass}
                style={getCompoundStyle({
                    node,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                id={id}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
            >
                {emptyState ? (
                    children
                ) : (
                    <>
                        {text && (
                            <p
                                style={getCompoundStyle({
                                    otherStyle: titleStyles,
                                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "titleStyles")
                                })}
                            >
                                {text}
                            </p>
                        )}
                        {variationData?.map((variation, index) => (
                            <Variation
                                key={index}
                                node={node}
                                variation={variation}
                                formatC={formatC}
                                builder
                                nodeState={nodeState}
                            />
                        ))}
                    </>
                )}
            </div>
        );
    } else {
        const { style, ...rest } = attr || {};

        const wrapperStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const titleStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: titleStyles,
            stateManager,
            paths: ["titleStyles"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        return (
            <div {...rest} {...actionCallbacks} className={productVariationsClass} ref={setElement} style={wrapperStyle}>
                {variationData && variationData.length > 0 && !isSimple ? (
                    <>
                        {node.text && <p style={titleStyle}>{node.text}</p>}
                        {variationData.map((variation, index) => (
                            <Variation
                                key={index}
                                node={node}
                                variation={variation}
                                formatC={formatC}
                                onSelect={renderProps?.onSelect}
                                disabled={disabled}
                            />
                        ))}
                    </>
                ) : (
                    children
                )}
            </div>
        );
    }
};

export default BuilderProductVariations;
